/**
 * What a stage-activity drawer says when the API refuses the save.
 *
 * Extracted from the email activity (ENG-3962) so the survey drawer answers a
 * refusal with the same words. Both save through `ats_job_stage_actions`, and
 * both were reading the message off `result.errors` — a key that is only
 * populated on one of the three envelopes a refusal actually arrives in, so a
 * real reason was routinely replaced by the generic line. That is how
 * "Sender's mailbox is not connected" reached a tester as "Something went
 * wrong" (ENG-3962 for email, ENG-4001 for survey).
 *
 * The three envelopes, all of which land here:
 *   - RTK Query, non-2xx — `{ error: { errors: [...] } }`, the axios error body.
 *   - RTK Query, 2xx carrying errors — `{ error: [...] }`, from `axiosAtsQuery`.
 *   - TanStack (the send-survey drawer) — `{ errors: [...] }`, built by hand.
 *
 * The body still cannot simply be echoed. `ats_job_stage_actions` re-raises
 * everything it catches as `unprocessable_entity` carrying the exception's own
 * message, so the first entry arrives in one of three shapes: a sentence
 * written for the user ("Sender's mailbox is not connected. Please request…"),
 * that sentence behind machine prefixes ("Validation failed: err_invalid_syntax:
 * Content contains invalid syntax…"), or nothing but a code
 * ("err_invalid_options"), with Rails' record-lookup message as a fourth when
 * an id does not resolve.
 *
 * So: strip the prefixes, and show what is left only if it reads as a sentence.
 * A code or a lookup internal tells a recruiter nothing and would dress an
 * unrelated failure up as an explanation, so those keep the generic line. The
 * toast therefore carries either the reason for this failure or no reason at
 * all — never the wrong one.
 */

export const GENERIC_ACTIVITY_FAILURE =
  'Something went wrong please try again later or contact support@kula.ai';

/** A backend code with no sentence after it — `err_invalid_options`. */
const BARE_ERROR_CODE = /^err_[a-z0-9_]*$/;
/** ActiveRecord's own lookup message — "Couldn't find User with 'id'=15". */
const RECORD_NOT_FOUND = /^couldn't find /i;

/** The result shape the activity mutations resolve to, across both data layers. */
export interface ActivitySaveResult {
  data?: { id?: string | number } | null;
  error?: unknown;
  errors?: unknown;
}

const firstEntry = (value: unknown): unknown => {
  if (Array.isArray(value)) return value[0];
  const nested = (value as { errors?: unknown })?.errors;
  return Array.isArray(nested) ? nested[0] : undefined;
};

/**
 * The first server error, whichever envelope carried it.
 *
 * `error` is read before `errors` because RTK Query owns the former: when both
 * are somehow present the one the data layer set is the real refusal.
 */
export const firstActivitySaveError = (
  result: ActivitySaveResult | unknown
): unknown =>
  firstEntry((result as ActivitySaveResult)?.error) ??
  firstEntry((result as ActivitySaveResult)?.errors) ??
  firstEntry(result);

/** The reason to show for a refused save, or the generic line when there is none. */
export const activitySaveFailureMessage = (
  result: ActivitySaveResult | unknown
): string => {
  const first = firstActivitySaveError(result);
  if (typeof first !== 'string') return GENERIC_ACTIVITY_FAILURE;

  const message = first
    .replace(/^validation failed:\s*/i, '')
    .replace(/^err_[a-z0-9_]*:\s*/, '')
    .trim();

  const isSentence =
    !!message &&
    !BARE_ERROR_CODE.test(message) &&
    !RECORD_NOT_FOUND.test(message);

  return isSentence ? message : GENERIC_ACTIVITY_FAILURE;
};
